import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";

import * as api from "../api.js";
import {
  PageHeader,
  Loading,
  ErrorBanner,
  VersionBadge,
  PromptBody,
  VersionHistory,
  Tabs,
} from "../components.jsx";
import { useLibraryEnv, useEnvPrefix } from "../LibraryEnvContext.jsx";

export default function LibraryProtocolDetail() {
  const { category, id } = useParams();
  const { env, isLive } = useLibraryEnv();
  const prefix = useEnvPrefix();
  const [doc, setDoc] = useState(null);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("content");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);

  // Edit form state (populated when entering edit mode)
  const [form, setForm] = useState({
    name: "",
    description: "",
    specialties: [],
    customSpecialty: "",
    body_parts: "",
    required_tools: [],
    content: "",
  });

  const [existingSpecialties, setExistingSpecialties] = useState([
    "oncology",
    "orthopedics",
  ]);
  const [allTools, setAllTools] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, histRes] = await Promise.all([
        api.getProtocol(category, id, env),
        api.getProtocolHistory(category, id).catch(() => ({ data: [] })),
      ]);
      setDoc(res.data);
      setHistory(histRes.data || []);
      setError(null);
      // Populate edit form from loaded doc (don't clobber unsaved edits)
      if (!editing && res.data) {
        setForm({
          name: res.data.name || "",
          description: res.data.description || "",
          specialties: [...(res.data.specialties || [])],
          customSpecialty: "",
          body_parts: (res.data.body_parts || []).join(", "),
          required_tools: [...(res.data.required_tools || [])],
          content: res.data.content || "",
        });
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, id, env]);

  useEffect(() => {
    load();
  }, [load]);

  // Also discover existing specialties from the broader protocol set
  // and load the tools catalog so the required-tools picker has data.
  useEffect(() => {
    (async () => {
      try {
        const [protoRes, toolsRes] = await Promise.all([
          api.listProtocols({}),
          api.listTools().catch(() => ({ data: [] })),
        ]);
        const specs = new Set(["oncology", "orthopedics"]);
        for (const p of protoRes.data || []) {
          for (const s of p.specialties || []) specs.add(s);
        }
        setExistingSpecialties([...specs].sort());
        setAllTools(toolsRes.data || []);
      } catch {
        // ignore
      }
    })();
  }, []);

  const toggleRequiredTool = (toolId) => {
    setForm((f) => ({
      ...f,
      required_tools: f.required_tools.includes(toolId)
        ? f.required_tools.filter((x) => x !== toolId)
        : [...f.required_tools, toolId],
    }));
  };

  const toolMap = useMemo(() => {
    const m = new Map();
    for (const t of allTools) m.set(t._id, t);
    return m;
  }, [allTools]);

  const toggleSpecialty = (s) => {
    setForm((f) => ({
      ...f,
      specialties: f.specialties.includes(s)
        ? f.specialties.filter((x) => x !== s)
        : [...f.specialties, s],
    }));
  };

  const addCustomSpecialty = () => {
    const s = form.customSpecialty.trim().toLowerCase();
    if (!s) return;
    if (!form.specialties.includes(s)) {
      setForm((f) => ({
        ...f,
        specialties: [...f.specialties, s],
        customSpecialty: "",
      }));
      if (!existingSpecialties.includes(s)) {
        setExistingSpecialties((arr) => [...arr, s].sort());
      }
    } else {
      setForm((f) => ({ ...f, customSpecialty: "" }));
    }
  };

  const enterEditMode = () => {
    if (doc) {
      setForm({
        name: doc.name || "",
        description: doc.description || "",
        specialties: [...(doc.specialties || [])],
        customSpecialty: "",
        body_parts: (doc.body_parts || []).join(", "),
        required_tools: [...(doc.required_tools || [])],
        content: doc.content || "",
      });
    }
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError(null);
  };

  const handleRestore = async (version) => {
    if (
      !window.confirm(
        `Restore version v${version} as a new draft?\n\nThis creates a new draft version whose content matches v${version}. You can then publish it via Revisions to make it the new live.`
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.restoreProtocolVersion(category, id, version, {
        mode: "draft",
        author: "admin",
        reason: `UI restore from v${version}`,
      });
      setSelectedVersion(null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (form.specialties.length === 0) {
      setError(new Error("At least one specialty must be selected"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body_parts = form.body_parts
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      await api.writeProtocol(category, id, {
        content: form.content,
        mode: "draft",
        name: form.name.trim(),
        description: form.description.trim(),
        specialties: form.specialties,
        body_parts: body_parts.length > 0 ? body_parts : null,
        required_tools: form.required_tools,
        base_version: doc?.version ?? null,
        author: "admin",
        commit_msg: `Edit ${category}/${id} from admin UI`,
      });

      setEditing(false);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={doc?.name || id}
        subtitle={doc?.description || `${category} protocol`}
        breadcrumbs={[
          { label: "Library", to: `${prefix}/platform` },
          { label: "Agent Rules", to: `${prefix}/protocols` },
          { label: category },
          { label: id },
        ]}
        actions={
          <div className="btn-row">
            {doc && <VersionBadge version={doc.version} />}
            {isLive ? null : !editing ? (
              <button
                className="btn primary"
                onClick={enterEditMode}
                disabled={!doc || busy}
              >
                Edit
              </button>
            ) : (
              <>
                <button className="btn" onClick={cancelEdit} disabled={busy}>
                  Cancel
                </button>
                <button className="btn primary" onClick={save} disabled={busy}>
                  {busy ? "Saving..." : "Save draft"}
                </button>
              </>
            )}
          </div>
        }
      />

      <Tabs
        tabs={[
          { id: "content", label: "Content" },
          { id: "history", label: `History (${history.length})` },
        ]}
        active={tab}
        onChange={setTab}
      />

      <div className="tab-content">
        <ErrorBanner error={error} />

        {loading ? (
          <Loading />
        ) : !doc ? (
          <div className="empty-state">
            <h3>Protocol not found</h3>
            <p>
              <Link to={`${prefix}/protocols`}>Back to protocols</Link>
            </p>
          </div>
        ) : tab === "history" ? (
          <div className="card">
            <VersionHistory
              versions={history}
              onSelect={(v) => setSelectedVersion(v)}
            />
          </div>
        ) : editing ? (
          <EditForm
            form={form}
            setForm={setForm}
            existingSpecialties={existingSpecialties}
            toggleSpecialty={toggleSpecialty}
            addCustomSpecialty={addCustomSpecialty}
            toggleRequiredTool={toggleRequiredTool}
            allTools={allTools}
            category={category}
            protocolId={id}
          />
        ) : (
          <ReadOnlyView doc={doc} category={category} toolMap={toolMap} />
        )}
      </div>

      {selectedVersion && (
        <div
          className="modal-backdrop"
          onClick={() => setSelectedVersion(null)}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 900, width: "92%" }}
          >
            <h2>Version v{selectedVersion.version}</h2>
            <div className="muted small mb-16">
              by {selectedVersion.created_by || "unknown"} ·{" "}
              {selectedVersion.commit_msg || <span className="subtle">no message</span>}
            </div>
            <pre
              style={{
                maxHeight: "55vh",
                overflow: "auto",
                padding: 12,
                margin: 0,
                background: "#fafafa",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-sm)",
                fontFamily: "var(--font-mono)",
                fontSize: 12,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {selectedVersion.content}
            </pre>
            <div
              className="btn-row"
              style={{ marginTop: 20, justifyContent: "flex-end" }}
            >
              <button className="btn" onClick={() => setSelectedVersion(null)}>
                Close
              </button>
              {!isLive && !selectedVersion.is_draft && (
                <button
                  className="btn primary"
                  onClick={() => handleRestore(selectedVersion.version)}
                  disabled={busy}
                >
                  Restore as draft
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ReadOnlyView({ doc, category, toolMap }) {
  const requiredTools = doc.required_tools || [];
  return (
    <>
      <div className="card">
        <div className="card-header">
          <h3>Metadata</h3>
        </div>
        <table className="table" style={{ border: "none" }}>
          <tbody>
            <tr>
              <th style={{ width: "25%" }}>Category</th>
              <td className="mono">{category}</td>
            </tr>
            <tr>
              <th>Protocol ID</th>
              <td className="mono">{doc.protocol_id}</td>
            </tr>
            <tr>
              <th>Name</th>
              <td>{doc.name || <span className="subtle">—</span>}</td>
            </tr>
            <tr>
              <th>Description</th>
              <td>{doc.description || <span className="subtle">—</span>}</td>
            </tr>
            <tr>
              <th>Specialties</th>
              <td>
                <div className="btn-row">
                  {(doc.specialties || []).length === 0 ? (
                    <span className="subtle">—</span>
                  ) : (
                    (doc.specialties || []).map((s) => (
                      <span key={s} className="badge badge-accent">
                        {s}
                      </span>
                    ))
                  )}
                </div>
              </td>
            </tr>
            {doc.body_parts && doc.body_parts.length > 0 && (
              <tr>
                <th>Body parts</th>
                <td>
                  <div className="btn-row">
                    {doc.body_parts.map((b) => (
                      <span key={b} className="badge badge-neutral">
                        {b}
                      </span>
                    ))}
                  </div>
                </td>
              </tr>
            )}
            <tr>
              <th>Length</th>
              <td>{(doc.content?.length || 0).toLocaleString()} chars</td>
            </tr>
            <tr>
              <th>Version</th>
              <td>v{doc.version}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>
            Required tools
            <span className="muted small" style={{ fontWeight: 400, marginLeft: 6 }}>
              {requiredTools.length === 0
                ? "none declared"
                : `${requiredTools.length} declared`}
            </span>
          </h3>
        </div>
        <p className="muted small mb-16">
          Tools this rule assumes the calling agent has available. The
          runtime does not auto-inject them — agents must have these in
          their own tool list or the rule won't work as authored.
        </p>
        {requiredTools.length === 0 ? (
          <div className="muted small">No tool requirements set.</div>
        ) : (
          <ul className="required-tools-list">
            {requiredTools.map((tid) => {
              const def = toolMap?.get?.(tid);
              return (
                <li key={tid}>
                  <span className="mono" style={{ fontWeight: 600 }}>
                    {tid}
                  </span>
                  <span className="muted small">
                    {def?.description || (
                      <span className="subtle">Not in tools catalog</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Content</h3>
        </div>
        <PromptBody content={doc.content} />
      </div>
    </>
  );
}

function EditForm({
  form,
  setForm,
  existingSpecialties,
  toggleSpecialty,
  addCustomSpecialty,
  toggleRequiredTool,
  allTools,
  category,
  protocolId,
}) {
  return (
    <>
      <div className="card">
        <div className="card-header">
          <h3>Metadata</h3>
        </div>

        <div className="form-field">
          <label>Category</label>
          <input type="text" value={category} readOnly style={{ background: "#f6f6f6" }} />
          <div className="help">
            Category is not editable. To move a protocol to another category, create a new
            protocol in that category and delete this one.
          </div>
        </div>

        <div className="form-field">
          <label>Protocol ID</label>
          <input type="text" value={protocolId} readOnly style={{ background: "#f6f6f6" }} />
        </div>

        <div className="form-field">
          <label>Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </div>

        <div className="form-field">
          <label>Description</label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </div>

        <div className="form-field">
          <label>Body parts (comma separated)</label>
          <input
            type="text"
            value={form.body_parts}
            onChange={(e) => setForm((f) => ({ ...f, body_parts: e.target.value }))}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Specialties</h3>
        </div>
        <div className="btn-row mb-8" style={{ flexWrap: "wrap" }}>
          {existingSpecialties.map((s) => {
            const on = form.specialties.includes(s);
            return (
              <button
                key={s}
                className={`btn sm ${on ? "primary" : ""}`}
                onClick={() => toggleSpecialty(s)}
              >
                {on ? "✓ " : ""}
                {s}
              </button>
            );
          })}
        </div>
        <div className="btn-row">
          <input
            type="text"
            placeholder="Add custom specialty..."
            value={form.customSpecialty}
            onChange={(e) =>
              setForm((f) => ({ ...f, customSpecialty: e.target.value }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addCustomSpecialty();
              }
            }}
            style={{ flex: 1, padding: "6px 10px", fontSize: 13 }}
          />
          <button className="btn sm" onClick={addCustomSpecialty}>
            Add
          </button>
        </div>
      </div>

      <RequiredToolsCard
        allTools={allTools}
        selected={form.required_tools}
        onToggle={toggleRequiredTool}
      />

      <div className="card">
        <div className="card-header">
          <h3>Content</h3>
        </div>
        <div className="form-field">
          <textarea
            value={form.content}
            onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
            spellCheck="false"
          />
          <div className="help">{form.content.length.toLocaleString()} chars</div>
        </div>
      </div>
    </>
  );
}

// ─── Required tools picker (rule editor card) ──────────────────────
function RequiredToolsCard({ allTools, selected, onToggle }) {
  const [search, setSearch] = React.useState("");
  const [category, setCategory] = React.useState("all");

  const categories = React.useMemo(() => {
    const seen = new Set(["all"]);
    for (const t of allTools) seen.add(t.category || "Other");
    return [...seen];
  }, [allTools]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return allTools.filter((t) => {
      if (category !== "all" && (t.category || "Other") !== category) return false;
      if (q) {
        const hay = `${t.name || t._id} ${t.description || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allTools, category, search]);

  return (
    <div className="card">
      <div className="card-header">
        <h3>
          Required tools
          <span className="muted small" style={{ fontWeight: 400, marginLeft: 6 }}>
            {selected.length === 0
              ? "none declared"
              : `${selected.length} declared`}
          </span>
        </h3>
      </div>
      <p className="muted small mb-16">
        Declare which tools the calling agent must have available for this
        rule to work. The runtime does NOT auto-inject them — this drives
        a "missing tools" lint on the agent editor so authors notice
        unmet dependencies before publishing.
      </p>
      <div
        className="btn-row mb-8"
        style={{ flexWrap: "wrap", gap: 6 }}
      >
        {categories.map((c) => (
          <button
            key={c}
            type="button"
            className={`btn sm ${category === c ? "primary" : ""}`}
            onClick={() => setCategory(c)}
          >
            {c === "all" ? "All" : c}
          </button>
        ))}
        <div className="spacer" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tools..."
          style={{ padding: "6px 10px", fontSize: 13, minWidth: 200 }}
        />
      </div>
      <div className="required-tools-grid">
        {filtered.length === 0 ? (
          <div className="muted small">No tools match the filter.</div>
        ) : (
          filtered.map((t) => {
            const on = selected.includes(t._id);
            return (
              <button
                key={t._id}
                type="button"
                className={`required-tool-chip ${on ? "on" : ""}`}
                onClick={() => onToggle(t._id)}
              >
                <span className="required-tool-chip-check">{on ? "✓" : "+"}</span>
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <div className="mono small" style={{ fontWeight: 600 }}>
                    {t._id}
                  </div>
                  {t.description && (
                    <div className="muted small">{t.description}</div>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
